import type { SmartBriefSharing } from "@unbox/api";
import { Check, Copy01, RefreshCw01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { Heading as AriaHeading } from "react-aria-components";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Switch } from "@/components/ui/switch";
import { useClipboard } from "@/hooks/use-clipboard";

interface StackedWithTeamAndLinkModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  sharingData?: SmartBriefSharing;
  onUpdateSharing: (isPublic: boolean, isPasswordProtected?: boolean, password?: string) => Promise<void>;
  briefTitle: string;
}

export const StackedWithTeamAndLinkModal = ({
  isOpen,
  onOpenChange,
  sharingData,
  onUpdateSharing,
  briefTitle,
}: StackedWithTeamAndLinkModalProps) => {
  const shareUrl = sharingData?.shareUrl ? `${window.location.origin}${sharingData.shareUrl}` : "";
  const [value, setValue] = useState(shareUrl);
  const [isPublic, setIsPublic] = useState(sharingData?.isPublic ?? false);
  const [isPasswordProtected, setIsPasswordProtected] = useState(sharingData?.isPasswordProtected ?? false);
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Track if user already had a password set (to avoid regenerating on modal open)
  const [hadExistingPassword, setHadExistingPassword] = useState(sharingData?.isPasswordProtected ?? false);
  const { copy, copied } = useClipboard();
  const { copy: copyPassword, copied: copiedPassword } = useClipboard();

  // Update values when sharingData changes (only when modal is open)
  useEffect(() => {
    if (!isOpen) return;
    setValue(shareUrl);
    if (sharingData) {
      setIsPublic(sharingData.isPublic);
      setIsPasswordProtected(sharingData.isPasswordProtected);
      setHadExistingPassword(sharingData.isPasswordProtected);
    }
  }, [shareUrl, sharingData, isOpen]);

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let result = "";
    for (let i = 0; i < 12; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Generate default password only when user toggles password protection ON for the first time
  // Don't generate if they already had a password saved (hadExistingPassword)
  useEffect(() => {
    if (!isOpen) return;
    if (isPasswordProtected && !password && !hadExistingPassword) {
      setPassword(generatePassword());
    }
  }, [isPasswordProtected, isOpen, password, hadExistingPassword]);

  const handleSave = async (closeModal: boolean = true) => {
    setIsLoading(true);
    try {
      await onUpdateSharing(isPublic, isPasswordProtected, password);
      // Don't clear password field after save to maintain persistence
      if (closeModal) {
        onOpenChange(false);
      }
    } catch (error) {
      console.error("Failed to update sharing:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render anything when closed
  if (!isOpen) return null;

  const handleSaveAndClose = async () => {
    await handleSave(true);
  };

  const handleCancel = () => {
    // Reset to original values
    setIsPublic(sharingData?.isPublic ?? false);
    setIsPasswordProtected(sharingData?.isPasswordProtected ?? false);
    setHadExistingPassword(sharingData?.isPasswordProtected ?? false);
    setPassword("");
    onOpenChange(false);
  };

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} isDismissable>
      <Modal>
        <Dialog>
          <div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-none sm:max-w-100">
            <div className="flex flex-row items-end justify-center -space-x-4 px-4 pt-5 sm:px-6 sm:pt-6">
              <Avatar
                size="lg"
                src="https://www.untitledui.com/images/avatars/transparent/caitlyn-king?bg=%23E0E0E0"
                alt="Caitlyn King"
              />
              <div className="relative z-10 inline-flex rounded-full ring-[1.5px] ring-bg-primary">
                <Avatar
                  size="xl"
                  src="https://www.untitledui.com/images/avatars/transparent/sienna-hewitt?bg=%23E0E0E0"
                  alt="Sienna Hewitt"
                />
              </div>
              <Avatar
                size="lg"
                src="https://www.untitledui.com/images/avatars/transparent/olly-schroeder?bg=%23E0E0E0"
                alt="Olly Schroeder"
              />
            </div>
            <div className="z-10 flex flex-col items-center justify-center gap-0.5 px-4 pt-5 sm:px-6 sm:pt-6">
              <AriaHeading slot="title" className="text-center text-md font-semibold text-primary">
                Share "{briefTitle}"
              </AriaHeading>
              <p className="text-center text-sm text-tertiary">Control who can access this smart brief.</p>
            </div>

            {/* Public Link Toggle */}
            <div className="px-4 sm:px-6 pt-4">
              <div className="py-4 bg-secondary rounded-lg inline-flex flex-col justify-start items-start gap-4 w-full px-4">
                <div className="self-stretch inline-flex justify-start items-start gap-3">
                  <Switch checked={isPublic} onCheckedChange={setIsPublic} />
                  <div className="flex-1 inline-flex flex-col justify-start items-start gap-0.5">
                    <div className="self-stretch text-secondary text-base font-medium leading-normal">
                      Enable public link
                    </div>
                    <div className="self-stretch text-tertiary text-sm font-normal leading-tight">
                      {isPublic ? "Anyone with the link can view this brief" : "Only you can access this brief"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Share URL and options - Only visible when public */}
            {isPublic && (
              <>
                <div className="flex flex-row items-end justify-end gap-1 px-4 sm:px-6 pt-4">
                  <Input
                    isReadOnly
                    label="Share link"
                    value={shareUrl || "Link will be generated when you save"}
                    onChange={setValue}
                    inputClassName="py-2.5!"
                  />
                  <Button
                    size="md"
                    color="tertiary"
                    onClick={() => copy(shareUrl)}
                    iconLeading={copied ? Check : Copy01}
                    isDisabled={!shareUrl}
                  />
                </div>

                {/* Password Protection */}
                <div className="px-4 sm:px-6 pt-3">
                  <div className="py-4 bg-secondary rounded-lg inline-flex flex-col justify-start items-start gap-4 w-full px-4">
                    <div className="self-stretch inline-flex justify-start items-start gap-3">
                      <Switch checked={isPasswordProtected} onCheckedChange={setIsPasswordProtected} />
                      <div className="flex-1 inline-flex flex-col justify-start items-start gap-0.5">
                        <div className="self-stretch text-secondary text-base font-medium leading-normal">
                          Password protect
                        </div>
                        <div className="self-stretch text-tertiary text-sm font-normal leading-tight">
                          Require a password to view this brief
                        </div>
                      </div>
                    </div>

                    {isPasswordProtected && (
                      <div className="self-stretch inline-flex justify-start items-end gap-1">
                        <Input
                          value={password}
                          onChange={setPassword}
                          placeholder={hadExistingPassword ? "Password set (leave blank to keep)" : "Enter password"}
                          type="text"
                          inputClassName="py-2.5!"
                          className="flex-1"
                        />
                        <Button
                          size="md"
                          color="tertiary"
                          onClick={() => copyPassword(password)}
                          iconLeading={copiedPassword ? Check : Copy01}
                        />
                        <Button
                          size="md"
                          color="tertiary"
                          onClick={() => setPassword(generatePassword())}
                          iconLeading={RefreshCw01}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            <div className="pt-6 sm:pt-8">
              <div className="z-10 flex flex-1 flex-col-reverse gap-3 border-t border-secondary p-4 *:grow sm:grid sm:grid-cols-2 sm:p-6">
                <Button color="secondary" size="lg" onClick={handleCancel} disabled={isLoading}>
                  Cancel
                </Button>
                <Button color="primary" size="lg" onClick={handleSaveAndClose} disabled={isLoading}>
                  {isLoading ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
